import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../store/authStore';
import axios from 'axios';

import { 
  getEbooksWithChapters, 
  getSingleEbooks, 
  getTrendingEbooks,
  searchEbooks,
  getEbookById,
  getEbookBySlug,
  getEbooksByCategory,
  getEbooksByAuthor,
  toggleEbookLike,
  getSimilarEbooks,
  getEbooksByLanguage,
  findEbooksByGenreName,
  findEbooksByGenreSlug,
  useCategoryMetaBySlug,
} from '../api/ebooksAPI';
import { useEffect, useState } from 'react';

export { useCategoryMetaBySlug };

// Hook for fetching ebooks with chapters
export const useEbooksWithChapters = (page = 1, limit = 10) => {
  const { setError } = useAuthStore();
  
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['ebooksWithChapters', page, limit],
    () => getEbooksWithChapters(page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch ebooks with chapters';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
    }
  );
  
  return {
    ebooksWithChapters: data?.data || [],
    pagination: data?.pagination,
    isLoading,
    error,
    refetchEbooksWithChapters: refetch
  };
};

// Hook for fetching single ebooks
export const useSingleEbooks = (page = 1, limit = 10) => {
  const { setError } = useAuthStore();
  
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['singleEbooks', page, limit],
    () => getSingleEbooks(page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch single ebooks';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
    }
  );
  
  return {
    singleEbooks: data?.data || [],
    pagination: data?.pagination,
    isLoading,
    error,
    refetchSingleEbooks: refetch
  };
};

// Hook for fetching trending ebooks
export const useTrendingEbooks = (page = 1, limit = 10) => {
  const { setError } = useAuthStore();
  
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['trendingEbooks', page, limit],
    () => getTrendingEbooks(page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch trending ebooks';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
    }
  );
  
  return {
    trendingEbooks: data?.data || [],
    pagination: data?.pagination,
    isLoading,
    error,
    refetchTrendingEbooks: refetch
  };
};

// Hook for searching ebooks
export const useSearchEbooks = (query, page = 1, limit = 10) => {
  const { setError } = useAuthStore();
  
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['searchEbooks', query, page, limit],
    () => searchEbooks(query, page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to search ebooks';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
      enabled: !!query, // Only run the query if there's a search query
    }
  );
  
  return {
    searchResults: data?.data || [],
    pagination: data?.pagination,
    isLoading,
    error,
    refetchSearchResults: refetch
  };
};

// Hook for fetching ebook by ID
export const useEbookById = (id) => {
  const { setError } = useAuthStore();
  
  const {
    data: ebook,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['ebook', id],
    () => getEbookById(id),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch ebook details';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
      enabled: !!id,
    }
  );
  
  useEffect(() => {
    console.log('Ebook ID:', id);
    console.log('Is query enabled:', !!id);
  }, [id]);
  
  return {
    ebook,
    isLoading,
    error,
    refetchEbook: refetch
  };
};

// Hook for fetching ebook by slug
export const useEbookBySlug = (slug) => {
  const { setError } = useAuthStore();
  
  const {
    data: ebook,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['ebookBySlug', slug],
    () => getEbookBySlug(slug),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch ebook details';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
      enabled: !!slug,
    }
  );
  
  return {
    ebook,
    isLoading,
    error,
    refetchEbook: refetch
  };
};

// Hook for fetching ebooks by category
export const useEbooksByCategory = (categoryId, page = 1, limit = 10) => {
  const { setError } = useAuthStore();
  
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['ebooksByCategory', categoryId, page, limit],
    () => getEbooksByCategory(categoryId, page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch ebooks by category';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
      enabled: !!categoryId,
    }
  );
  
  return {
    ebooksByCategory: data?.data || [],
    pagination: data?.pagination,
    isLoading,
    error,
    refetchEbooksByCategory: refetch
  };
};

// Hook for fetching ebooks by author
export const useEbooksByAuthor = (author, page = 1, limit = 10) => {
  const { setError } = useAuthStore();
  
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['ebooksByAuthor', author, page, limit],
    () => getEbooksByAuthor(author, page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch ebooks by author';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
      enabled: !!author,
    }
  );
  
  return {
    ebooksByAuthor: data?.data || [],
    pagination: data?.pagination,
    isLoading,
    error,
    refetchEbooksByAuthor: refetch
  };
};

// Hook for toggling ebook like status
export const useToggleEbookLike = () => {
  const { setError } = useAuthStore();
  const queryClient = useQueryClient();
  
  const mutation = useMutation(
    (id) => toggleEbookLike(id),
    {
      onSuccess: (data, variables) => {
        // Invalidate and refetch relevant queries
        queryClient.invalidateQueries(['ebook', variables]);
        queryClient.invalidateQueries(['ebookBySlug']);
        queryClient.invalidateQueries('ebooksWithChapters');
        queryClient.invalidateQueries('singleEbooks');
        queryClient.invalidateQueries('trendingEbooks');
        
        toast.success(data.message);
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to toggle like status';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    }
  );
  
  return {
    toggleLike: mutation.mutate,
    isLoading: mutation.isLoading,
    error: mutation.error
  };
};


export const useSimilarEbooks = (ebookId, limit = 10) => {
    const { setError } = useAuthStore();
    
    const {
      data: similarEbooks,
      isLoading,
      error,
      refetch
    } = useQuery(
      ['similarEbooks', ebookId, limit],
      () => getSimilarEbooks(ebookId, limit),
      {
        onError: (error) => {
          const errorMessage = error?.response?.data?.error || 'Failed to fetch similar ebooks';
          setError(errorMessage);
          toast.error(errorMessage);
        },
        retry: 1,
        staleTime: 300000, // 5 minutes
        cacheTime: 3600000, // 1 hour
        enabled: !!ebookId,
      }
    );
    
    return {
      similarEbooks: similarEbooks || [],
      isLoading,
      error,
      refetchSimilarEbooks: refetch
    };
  };

  // Add this to your hooks file
export const useEbooksByLanguage = (language = 'english', page = 1, limit = 10) => {
  const { setError } = useAuthStore();
  
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['ebooksByLanguage', language, page, limit],
    () => getEbooksByLanguage(language, page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.error || 'Failed to fetch ebooks by language';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled: !!language,
    }
  );
  
  return {
    ebooksByLanguage: data?.data || [],
    pagination: data?.pagination,
    isLoading,
    error,
    refetchEbooksByLanguage: refetch
  };
};

export const useEbooksByGenreName = (genreName, options = {}) => {
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50, // Changed default limit to 50
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });
  
  const { page, limit, sortBy, sortOrder } = pagination;
  
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['ebooks', 'genre', genreName, page, limit, sortBy, sortOrder],
    () => findEbooksByGenreName(genreName, page, limit, sortBy, sortOrder),
    {
      enabled: !!genreName,
      keepPreviousData: true,
      onError: (err) => {
        toast.error(err.message || 'Failed to load ebooks for this genre');
      },
      ...options,
    }
  );
  
  // Extract ebooks and pagination data
  const ebooks = data?.items || [];
  const paginationData = data?.pagination || {
    page: 1,
    limit: 50, // Changed default to 50
    totalItems: 0,
    totalPages: 0
  };
  
  // Helper functions for pagination
  const setPage = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };
  
  const setLimit = (newLimit) => {
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };
  
  const setSorting = (newSortBy, newSortOrder = 'DESC') => {
    setPagination(prev => ({
      ...prev,
      sortBy: newSortBy,
      sortOrder: newSortOrder,
      page: 1
    }));
  };
  
  return {
    ebooks,
    pagination: paginationData,
    isLoading,
    error,
    refetch,
    setPage,
    setLimit,
    setSorting
  };
};

export const useEbooksByGenreSlug = (genreSlug, options = {}) => {
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50, // Changed default limit to 50
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });
  
  const { page, limit, sortBy, sortOrder } = pagination;
  
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['ebooks', 'genre-slug', genreSlug, page, limit, sortBy, sortOrder],
    () => findEbooksByGenreSlug(genreSlug, page, limit, sortBy, sortOrder),
    {
      enabled: !!genreSlug,
      keepPreviousData: true,
      onError: (err) => {
        toast.error(err.message || 'Failed to load ebooks for this genre slug');
      },
      ...options,
    }
  );
  
  // Extract ebooks and pagination data
  const ebooks = data?.items || [];
  const paginationData = data?.pagination || {
    page: 1,
    limit: 50, // Changed default to 50
    totalItems: 0,
    totalPages: 0
  };
  
  // Helper functions for pagination
  const setPage = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };
  
  const setLimit = (newLimit) => {
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };
  
  const setSorting = (newSortBy, newSortOrder = 'DESC') => {
    setPagination(prev => ({
      ...prev,
      sortBy: newSortBy,
      sortOrder: newSortOrder,
      page: 1
    }));
  };
  
  return {
    ebooks,
    pagination: paginationData,
    isLoading,
    error,
    refetch,
    setPage,
    setLimit,
    setSorting
  };
};